import {onScopeDispose} from "vue";

type SocketSubscriber = (data: unknown) => void;

interface Registration {
    subscribers: Set<SocketSubscriber>;
    teardown: () => void;
}

/**
 * One registration per event name, shared by every caller.
 *
 * The Electron bridge binds a new pusher handler on each socketListenEvent
 * call, and its teardown calls `unbind(event)` without the handler — which
 * drops *every* handler of that event. Two independent listeners would
 * therefore each receive duplicated payloads, and the first one to unmount
 * would silence the others. Registering once and fanning out here avoids both.
 */
const registrations = new Map<string, Registration>();

/**
 * Listen to a broadcast event of the organization channel, which the system
 * store subscribes to right after authentication.
 *
 * No-op outside Electron: the browser build has no socket bridge.
 */
const useOrganizationSocketEvent = <T>(event: string, handler: (data: T) => void): void => {
    if (!window.ipcRenderer?.socketListenEvent) {
        return;
    }

    let registration = registrations.get(event);

    if (!registration) {
        const subscribers = new Set<SocketSubscriber>();
        const teardown = window.ipcRenderer.socketListenEvent<T>(event, (_ipcEvent, payload) => {
            subscribers.forEach((subscriber) => subscriber(payload?.data));
        });

        registration = {subscribers, teardown};
        registrations.set(event, registration);
    }

    const subscriber: SocketSubscriber = (data) => handler(data as T);

    registration.subscribers.add(subscriber);

    onScopeDispose(() => {
        const current = registrations.get(event);

        if (!current) {
            return;
        }

        current.subscribers.delete(subscriber);

        if (!current.subscribers.size) {
            current.teardown();
            registrations.delete(event);
        }
    });
};

export default useOrganizationSocketEvent;
